import {
  useMutation,
  useQueryClient,
  UseMutationOptions,
} from '@tanstack/react-query';
import { Brand, CreateBrandRequest } from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';
import { FieldQuery } from '../../types';

/**
 * Error code of the 409 a brand save gets when its `baseVersion` is no longer
 * the organization's latest brand version: the extraction job or another tab
 * wrote a newer one since the caller loaded its values.
 */
export const BRAND_VERSION_CONFLICT_ERROR_CODE = 5601;

export const isBrandVersionConflict = (error: unknown): boolean =>
  (error as { errorCode?: number } | null)?.errorCode === BRAND_VERSION_CONFLICT_ERROR_CODE;

export interface CreateBrandParams {
  data: CreateBrandRequest;
  fields?: FieldQuery<Brand>;
}

export const createBrand = async (params: CreateBrandParams): Promise<Brand> => {
  return await tinctApi.brands.createBrand(params.data, params.fields);
};

export const useCreateBrand = (
  options?: Omit<
    UseMutationOptions<Brand, ApiError, CreateBrandParams>,
    'mutationFn'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBrand,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.invalidateQueries({ queryKey: ['organization', args[1].data.organization.id] });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
};
